import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { FormGroup, Validators, FormControl } from "@angular/forms";
import { CstResponsivePopupComponentBase } from "src/app/common-components/resp-popup";
import { ZEITERFASSUNG_TYPE } from "../../../../formular-model/common/formular-group-types";
import { CommonKeys } from "../../../../formular-model/common/enums";
import { ItemRequest, ItemResult } from "../zeiterfassung.types";
import {
  ResourcesClientProvider,
  ResourceType,
  ResourceUserDto,
} from "src/app/web-api/web-api";
import { takeUntil, tap } from "rxjs/operators";
import { set } from "date-fns";
import { Subject } from "rxjs";

const midnight = (date: Date | undefined = undefined): Date => {
  if (date) {
    date.setHours(0, 0, 0, 0);
    return date;
  } else {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }
};

const combineDateTime = (dateSrc: Date, timeSrc: Date) => {
  if (
    dateSrc &&
    timeSrc &&
    dateSrc instanceof Date &&
    timeSrc instanceof Date
  ) {
    return set(dateSrc, {
      hours: timeSrc.getHours(),
      minutes: timeSrc.getMinutes(),
    });
  } else {
    console.error("Failed to combine date and time:", dateSrc, timeSrc);
    return midnight();
  }
};

const timeStringToDate = (date: string) => {
  const d = new Date();
  const [h, m] = date.split(":");
  d.setHours(Number.parseInt(h), Number.parseInt(m), 0, 0);
  return d;
};

const getCombinedDateString = (dateSrc: Date, timeSrc: Date) => {
  return combineDateTime(dateSrc, timeSrc).toISOString();
};

const stripZ = (date: string): Date =>
  date.endsWith("Z") ? new Date(date.slice(0, -1)) : new Date(date);

@Component({
  templateUrl: "./popup.component.html",
  styleUrls: ["./popup.component.scss"],
  standalone: false,
})
export class NewOrEditZeiterfassungPopupComponent
  extends CstResponsivePopupComponentBase<ItemRequest, ItemResult | null>
  implements OnInit, OnDestroy
{
  private destroy$ = new Subject<void>();
  private _res = inject(ResourcesClientProvider);

  public form!: FormGroup;

  public data!: ItemRequest;

  public get state() {
    return this.data.state;
  }
  public get zfData() {
    return this.data.data;
  }

  resources!: { label: string; value: ResourceUserDto }[];
  public selectedResource!: { label: string; value: ResourceUserDto };

  ngOnInit(): void {
    if (this.state === "edit") {
      this._res
        .getAll()
        .pipe(
          tap((result) => {
            this.resources = result.resources
              .filter((r) => r.type === ResourceType.User)
              .map((r) => {
                return { label: r.name, value: r };
              });
            this.selectedResource = this.resources?.find(
              (r) => r.value.id === this.zfData?.RessourcenID,
            );
          }),
          takeUntil(this.destroy$),
        )
        .subscribe({
          error: (err) => console.error(err),
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onInitialised(): void {
    if (this.state === "new") {
      this.form = new FormGroup(
        {
          name: new FormControl(null, Validators.required),
          datum: new FormControl(null, Validators.required),
          zuschlag: new FormControl(null, Validators.required),
          zone: new FormControl(null, Validators.required),
          arbeitszeitbeginn: new FormControl(null, Validators.required),
          arbeitszeitende: new FormControl(null, Validators.required),
          fahrtzeit: new FormControl(null, Validators.required),
          arbeitszeit: new FormControl(null, Validators.required),
          gesamtzeit: new FormControl(null, Validators.required),
        },
        Validators.required,
      );
    } else if (this.state === "edit" && this.zfData) {
      const startDate = stripZ(this.zfData.ArbeitszeitBeginn);
      const endDate = stripZ(this.zfData.ArbeitszeitEnde);

      this.form = new FormGroup(
        {
          name: new FormControl(this.zfData.RessourcenID, Validators.required),
          datum: new FormControl(startDate, Validators.required),
          zuschlag: new FormControl(this.zfData.Zuschlag, Validators.required),
          zone: new FormControl(this.zfData.Zone, Validators.required),
          arbeitszeitbeginn: new FormControl(startDate, Validators.required),
          arbeitszeitende: new FormControl(endDate, Validators.required),
          fahrtzeit: new FormControl(
            combineDateTime(startDate, timeStringToDate(this.zfData.Fahrtzeit)),
            Validators.required,
          ),
          arbeitszeit: new FormControl(
            combineDateTime(
              startDate,
              timeStringToDate(this.zfData.Arbeitszeit),
            ),
            Validators.required,
          ),
          gesamtzeit: new FormControl(
            combineDateTime(
              startDate,
              timeStringToDate(this.zfData.Gesamtzeit),
            ),
            Validators.required,
          ),
        },
        Validators.required,
      );
    } else {
      console.error(`Unknown state: ${this.state}`);
    }
  }

  public get canAccept() {
    return this.form.valid;
  }

  public onAccept() {
    if (!this.canAccept) return;

    this._res
      .getUser(this.form.get("name")?.value)
      .pipe(
        tap((user) => {
          const res = {
            [CommonKeys.Type]: ZEITERFASSUNG_TYPE,
            Name: user.firstname + " " + user.lastname,
            RessourcenID: user.id,
            ArbeitszeitBeginn: getCombinedDateString(
              this.form.get("datum")?.value,
              this.form.get("arbeitszeitbeginn")?.value,
            ),
            ArbeitszeitEnde: getCombinedDateString(
              this.form.get("datum")?.value,
              this.form.get("arbeitszeitende")?.value,
            ),
            Zuschlag: Number.parseInt(this.form.get("zuschlag")?.value, 10),
            Zone: Number.parseInt(this.form.get("zone")?.value, 10),
            Arbeitszeit: this.form.get("arbeitszeit")?.value,
            Fahrtzeit: this.form.get("fahrtzeit")?.value,
            Gesamtzeit: this.form.get("gesamtzeit")?.value,
          };

          this.close({ state: true, data: res });
        }),
      )
      .subscribe({ error: (error) => console.error(error) });
  }

  public onCancel() {
    this.close({ state: false, data: null });
  }
}
